// Which sign-in method this account is saved with — and the copy that tells the
// user to reuse it in the app.
//
// ## Why this exists (Tom 2026-07-16)
//
// The single most expensive moment in the funnel is the web→app hop: someone
// plays on web, saves with Google, installs the app, taps "Continue with
// Apple" — and lands in a BRAND-NEW account. Their streak, their coins, their
// live head-to-head: all still there, attached to a Google account they can no
// longer see. It reads as "the app lost my data", and it's unrecoverable
// without support.
//
// Everything needed to prevent it was already recorded and never shown:
// `providerData[0].providerId` live in SessionContext, `securedProvider`
// stamped at save (webBonuses.ts), `signInProvider` stamped server-side
// (index.ts). iOS even hints the provider back — but only via iCloud KVS, so
// only on a device that ALREADY signed in once. The first install, the one
// that matters, had nothing on either end.
//
// So: name the provider at every moment the user is about to cross over.
// Copy, not plumbing. See docs/onboarding-experience-2026-07.md §6.

/**
 * A Firebase providerId → the word the user actually saw on the button.
 *
 * Returns null for anonymous/unknown, which every caller reads as "say nothing"
 * — a wrong provider name is worse than no provider name, since it would send
 * someone to the wrong button with confidence.
 */
export function providerLabelFor(pid) {
  if (!pid) return null;
  if (pid.includes('google')) return 'Google';
  if (pid.includes('apple')) return 'Apple';
  if (pid.includes('password')) return 'email';
  return null;
}

/**
 * The one-line continuity hint, or null when we can't name the provider.
 *
 * Phrased as the instruction ("use X in the app"), not the fact ("you used
 * X") — the user isn't being informed, they're being told what to tap on a
 * screen they haven't seen yet.
 */
export function continuityLineFor(pid) {
  const label = providerLabelFor(pid);
  if (!label) return null;
  return label === 'email'
    ? 'Signed in with email — use the same email in the app and everything comes with you.'
    : `Signed in with ${label} — use ${label} in the app and everything comes with you.`;
}

/**
 * The provider phrased as an object of "with …": "Apple" / "Google" /
 * "your email". Null for anon/unknown. Shared by the two account-saved lines
 * below so they read naturally ("signed in with your email", not "with email").
 */
function withPhraseFor(pid) {
  const label = providerLabelFor(pid);
  if (!label) return null;
  return label === 'email' ? 'your email' : label;
}

/**
 * The "Account saved" success-sheet line (SaveAccountModal, item #7):
 * "You are signed in with Apple/Google/your email." Null → render nothing.
 */
export function signedInWithLineFor(pid) {
  const phrase = withPhraseFor(pid);
  return phrase ? `You are signed in with ${phrase}.` : null;
}

/**
 * The post-submit "All six locked in" cross-over reminder (Play.jsx, item #8):
 * "Remember that you signed in with Apple/Google/your email." Same intent as
 * `continuityLineFor` (steer them to the right button in the app) in the
 * shorter voice the locked-in card wants. Null → render nothing.
 */
export function rememberSignInLineFor(pid) {
  const phrase = withPhraseFor(pid);
  return phrase ? `Remember that you signed in with ${phrase}.` : null;
}
